import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { card, Btn, Modal, Field, Input, Textarea, Select, ImageUpload, Badge } from "./ui";
import { Plus, Pencil, Trash2, Eye, EyeOff, ChevronRight } from "lucide-react";

const SHAPES = [{ value: "portrait", label: "Vertical / Portrait" }, { value: "arch", label: "Arch" }, { value: "oval", label: "Oval" }, { value: "pill", label: "Pill" }];

export default function MenuManager() {
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState(null);
  const [dishes, setDishes] = useState([]);
  const [catModal, setCatModal] = useState(null);
  const [dishModal, setDishModal] = useState(null);

  const loadCats = () => api.get("/admin/categories").then(({ data }) => { setCats(data); if (!active && data[0]) selectCat(data[0]); }).catch(() => {});
  const selectCat = (c) => { setActive(c); api.get(`/public/menu/${c.id}`).then(({ data }) => setDishes(data.dishes || [])).catch(() => api.get("/admin/dishes").then(({ data }) => setDishes(data.filter((d) => d.category_id === c.id)))); };
  useEffect(() => { loadCats(); }, []);
  useEffect(() => { if (active) api.get("/admin/dishes").then(({ data }) => setDishes(data.filter((d) => d.category_id === active.id))); }, [active?.id]);

  const saveCat = async (c) => {
    if (c.id) await api.put(`/admin/categories/${c.id}`, c);
    else await api.post("/admin/categories", c);
    setCatModal(null); loadCats();
  };
  const delCat = async (id) => { if (!window.confirm("Delete category and keep dishes hidden?")) return; await api.delete(`/admin/categories/${id}`); setActive(null); loadCats(); };
  const saveDish = async (d) => {
    d.category_id = active.id;
    if (d.id) await api.put(`/admin/dishes/${d.id}`, d);
    else await api.post("/admin/dishes", d);
    setDishModal(null); const { data } = await api.get("/admin/dishes"); setDishes(data.filter((x) => x.category_id === active.id));
  };
  const delDish = async (id) => { await api.delete(`/admin/dishes/${id}`); setDishes((x) => x.filter((d) => d.id !== id)); };
  const toggleDish = async (d) => { await api.put(`/admin/dishes/${d.id}`, { visible: !d.visible }); setDishes((x) => x.map((y) => y.id === d.id ? { ...y, visible: !y.visible } : y)); };

  return (
    <div data-testid="admin-menu">
      <div className="flex items-center justify-between mb-6">
        <div><p className="text-gold/70 text-xs tracking-[0.25em] uppercase">Menu Management</p><h1 className="font-serif text-4xl text-ivory mt-1">Culinary Collection</h1></div>
        <Btn onClick={() => setCatModal({ shape: "portrait", visible: true })} data-testid="add-category"><Plus className="w-4 h-4" /> Category</Btn>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        <div className={`${card} p-3 lg:col-span-1 h-fit`}>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setActive(c)} className={`w-full flex items-center gap-3 p-2.5 rounded-lg text-left ${active?.id === c.id ? "bg-gold/15" : "hover:bg-white/5"}`}>
              <img src={c.image} alt="" className="w-9 h-9 rounded object-cover" />
              <span className="flex-1 text-sm text-ivory truncate">{c.name}</span>
              {!c.visible && <EyeOff className="w-3.5 h-3.5 text-ivory/30" />}
              <ChevronRight className="w-4 h-4 text-ivory/30" />
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {active ? (
            <div className={`${card} p-5`}>
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-4">
                  <img src={active.image} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <div><h2 className="font-serif text-2xl text-gold">{active.name}</h2><p className="text-ivory/50 text-sm">{active.tagline}</p></div>
                </div>
                <div className="flex gap-2">
                  <Btn variant="outline" onClick={() => setCatModal(active)}><Pencil className="w-4 h-4" /></Btn>
                  <Btn variant="danger" onClick={() => delCat(active.id)}><Trash2 className="w-4 h-4" /></Btn>
                  <Btn onClick={() => setDishModal({ visible: true, veg_type: "Non-Vegetarian" })} data-testid="add-dish"><Plus className="w-4 h-4" /> Dish</Btn>
                </div>
              </div>
              <div className="space-y-2">
                {dishes.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 bg-[#1C1210] rounded-lg p-3">
                    <img src={d.image} alt="" className="w-12 h-12 rounded object-cover" />
                    <div className="flex-1 min-w-0"><p className="text-ivory text-sm">{d.name}</p><p className="text-ivory/40 text-xs truncate">{d.description}</p></div>
                    {d.price && <Badge>{d.price}</Badge>}
                    <button onClick={() => toggleDish(d)} className="text-ivory/40 hover:text-gold p-1.5">{d.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                    <button onClick={() => setDishModal(d)} className="text-ivory/40 hover:text-gold p-1.5"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => delDish(d.id)} className="text-ivory/40 hover:text-red-400 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                {!dishes.length && <p className="text-ivory/40 text-sm py-6 text-center">No dishes yet. Add one.</p>}
              </div>
            </div>
          ) : <p className="text-ivory/40">Select a category.</p>}
        </div>
      </div>

      <CatModal data={catModal} onClose={() => setCatModal(null)} onSave={saveCat} />
      <DishModal data={dishModal} onClose={() => setDishModal(null)} onSave={saveDish} />
    </div>
  );
}

function CatModal({ data, onClose, onSave }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(data || {}); }, [data]);
  if (!data) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal open onClose={onClose} title={f.id ? "Edit Category" : "New Category"}>
      <Field label="Name"><Input value={f.name || ""} onChange={set("name")} placeholder="THE TANDOOR" /></Field>
      <Field label="Tagline"><Input value={f.tagline || ""} onChange={set("tagline")} placeholder="Smoke. Char. Spice." /></Field>
      <ImageUpload value={f.image} onChange={(v) => setF({ ...f, image: v })} label="Category Image (vertical works best)" />
      <Field label="Frame Shape"><Select value={f.shape || "portrait"} onChange={set("shape")} options={SHAPES} /></Field>
      <Field label="Visible"><Select value={f.visible ? "1" : "0"} onChange={(e) => setF({ ...f, visible: e.target.value === "1" })} options={[{ value: "1", label: "Shown" }, { value: "0", label: "Hidden" }]} /></Field>
      <Btn onClick={() => onSave(f)} className="w-full justify-center mt-2" data-testid="save-category">Save Category</Btn>
    </Modal>
  );
}

function DishModal({ data, onClose, onSave }) {
  const [f, setF] = useState({});
  useEffect(() => { setF(data || {}); }, [data]);
  if (!data) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Modal open onClose={onClose} title={f.id ? "Edit Dish" : "New Dish"}>
      <Field label="Dish Name"><Input value={f.name || ""} onChange={set("name")} /></Field>
      <Field label="Description"><Textarea value={f.description || ""} onChange={set("description")} /></Field>
      <ImageUpload value={f.image} onChange={(v) => setF({ ...f, image: v })} label="Dish Image" />
      <div className="grid grid-cols-2 gap-3">
        <Field label="Price (optional)"><Input value={f.price || ""} onChange={set("price")} placeholder="₹450" /></Field>
        <Field label="Min Quantity"><Input value={f.min_qty || ""} onChange={set("min_qty")} placeholder="Serves 10" /></Field>
      </div>
      <Field label="Serving Info"><Input value={f.serving || ""} onChange={set("serving")} placeholder="Per portion" /></Field>
      <Field label="Visible"><Select value={f.visible ? "1" : "0"} onChange={(e) => setF({ ...f, visible: e.target.value === "1" })} options={[{ value: "1", label: "Shown" }, { value: "0", label: "Hidden" }]} /></Field>
      <Btn onClick={() => onSave(f)} className="w-full justify-center mt-2" data-testid="save-dish">Save Dish</Btn>
    </Modal>
  );
}
